import dayjs, { ConfigType as DayjsConfigType } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _isInteger from 'lodash/isInteger';

dayjs.extend(utc);

export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
export const TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]?([ ]? [a|p]m)?$/;
export const DATE_REGEX =
  /^([0]?[1-9]|1[0-2]|\d{4})[-|/](0[1-9]|1[0-9]|2[0-9]|3[0-1])[-|/](\d{4}|[0]?[1-9]|1[1-2])/;
export const SLUG_REGEX = /^[a-za-z0-9]+[a-za-z0-9-_]*[a-za-z0-9]+$/;
export const ZIP_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const PHONE_REGEX = /(^(?:\([2-9]\d{2}\) ?|[2-9]\d{2}(?:-?| ?))[2-9]\d{2}[- ]?\d{4}$)/;
export const PHONE_US_REGEX = /(^(?:\([2-9]\d{2}\) ?|[2-9]\d{2}(?:-?| ?))[2-9]\d{2}[- ]?\d{4}$)/;
export const PHONE_US_WITH_PREFIX_REGEX =
  /(^(?:1?[- ]?)(?:\([2-9]\d{2}\) ?|[2-9]\d{2}(?:-?| ?))[2-9]\d{2}[- ]?\d{4}$)/;

// @see
//  https://github.com/NewAmsterdamLabs/commons/blob/adf84704317bddb21a68e01884240c6586d0dd30/commons-utils/src/main/java/com/zola/commons/util/ValidationUtils.java#L27-L28
export const INTERNATIONAL_PHONE_REGEX =
  /^\s?((\+[1-9]{1,4}[ -]*)|(\([0-9]{2,3}\)[ -]*)|([0-9]{2,4})[ -]*)*?[0-9]{3,4}?[ -]*[0-9]{3,4}?\s?/;

export const URL_REGEX =
  /(^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$)/;
export const EMOJI_REGEX =
  /(\u00a9|\u00ae|[\u2070-\u33ff]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
export const TOP_LEVEL_DOMAIN_REGEX =
  /^((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$/;
export const YOUTUBE_REGEX =
  /^(https?:)?(\/\/)?((www\.|m\.)?youtube(-nocookie)?\.com\/((watch)?\?(feature=\w*&)?vi?=|embed\/|vi?\/|e\/)|youtu.be\/)([\w-]{10,20})/i;
export const VIMEO_REGEX =
  /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/;
export const FIRST_AND_LAST_NAME_REGEX = /^([^ ]+) +(.+)$/;

export enum validationMsg {
  REQUIRED = 'Required',
  EMAIL = 'Invalid email address',
  EMOJI = 'Please remove any emoji characters',
  ZIP = 'Invalid zip code',
  PHONE = 'Invalid phone number',
  PHONE_US = 'Enter a valid U.S. phone number.',
  PASSWORD = 'Password must be at least 8 characters long',
  SLUG = 'Please use only letters, numbers, underscores and hyphens. Cannot start or end with a hyphen or underscore.',
  TIME = 'Invalid time',
  DATE = 'Invalid date',
  DATE_IS_BEFORE = 'Date cannot be before 2012',
  DATE_NON_VALID = 'Hmm… this does not look like a valid date.',
  DATE_PAST = 'Hmm… this looks like a past date.',
  URL = 'Please enter a valid web address.',
  GREATER_THAN_ZERO = 'Please enter a number greater than zero.',
  GREATER_THAN_OR_EQUAL_TO_ZERO = 'Please enter a number greater than or equal to zero.',
  NUMBER = 'Invalid number',
  INSTAGRAM_HANDLE = 'Must be a valid Instagram handle',
  YOUTUBE_OR_VIMEO = `Something's wrong! Check your link.`,
  FIRST_AND_LAST_NAME = 'Must include both first name and last name',
  MAX_GUEST_COUNT = 'Please enter a smaller number',
}

export const REQUIRED = (
  value: string | number | undefined
): validationMsg.REQUIRED | undefined => {
  if (typeof value === 'string') {
    return value && value.trim() ? undefined : validationMsg.REQUIRED;
  }
  return value ? undefined : validationMsg.REQUIRED;
};

export const EMAIL = (value: string | undefined): validationMsg.EMAIL | undefined =>
  value && !EMAIL_REGEX.test(value) ? validationMsg.EMAIL : undefined;

export const TIME = (value: string | undefined): validationMsg.TIME | undefined =>
  value && !TIME_REGEX.test(value) ? validationMsg.TIME : undefined;

export const DATE = (value: string | undefined): validationMsg.DATE | undefined =>
  value && !DATE_REGEX.test(value) ? validationMsg.DATE : undefined;

export const ZIP = (value: string | number | undefined): validationMsg.ZIP | undefined => {
  const normalizedValue = value?.toString();
  return normalizedValue && !ZIP_REGEX.test(normalizedValue) ? validationMsg.ZIP : undefined;
};

export const PHONE = (value: string | undefined): validationMsg.PHONE | undefined =>
  value && !PHONE_REGEX.test(value) ? validationMsg.PHONE : undefined;

export const PHONE_US = (value: string | undefined): validationMsg.PHONE_US | undefined =>
  value && !PHONE_US_REGEX.test(value) ? validationMsg.PHONE_US : undefined;

export const PHONE_US_WITH_PREFIX = (
  value: string | undefined
): validationMsg.PHONE_US | undefined =>
  value && !PHONE_US_WITH_PREFIX_REGEX.test(value) ? validationMsg.PHONE_US : undefined;

export const INTERNATIONAL_PHONE = (value: string | undefined): string | undefined =>
  value && !INTERNATIONAL_PHONE_REGEX.test(value) ? validationMsg.PHONE : undefined;

export const YOUTUBE_OR_VIMEO = (
  value: string | undefined
): validationMsg.YOUTUBE_OR_VIMEO | undefined =>
  value && !YOUTUBE_REGEX.test(value) && !VIMEO_REGEX.test(value)
    ? validationMsg.YOUTUBE_OR_VIMEO
    : undefined;

export const maxLength =
  (max: number) =>
  (value: string | undefined): string | undefined =>
    value && value.length > max ? `Must be ${max} characters or less` : undefined;

export const MAX_LENGTH_5 = maxLength(5);
export const MAX_LENGTH_15 = maxLength(15);
export const MAX_LENGTH_20 = maxLength(20);
export const MAX_LENGTH_25 = maxLength(25);
export const MAX_LENGTH_30 = maxLength(30);
export const MAX_LENGTH_35 = maxLength(35);
export const MAX_LENGTH_50 = maxLength(50);
export const MAX_LENGTH_40 = maxLength(40);
export const MAX_LENGTH_60 = maxLength(60);
export const MAX_LENGTH_80 = maxLength(80);
export const MAX_LENGTH_100 = maxLength(100);
export const MAX_LENGTH_150 = maxLength(150);
export const MAX_LENGTH_200 = maxLength(200);
export const MAX_LENGTH_250 = maxLength(250);
export const MAX_LENGTH_255 = maxLength(255);
export const MAX_LENGTH_400 = maxLength(400);
export const MAX_LENGTH_500 = maxLength(500);
export const MAX_LENGTH_650 = maxLength(650);
export const MAX_LENGTH_800 = maxLength(800);
export const MAX_LENGTH_1000 = maxLength(1000);
export const MAX_LENGTH_1500 = maxLength(1500);
export const MAX_LENGTH_2000 = maxLength(2000);
export const MAX_LENGTH_3500 = maxLength(3500);
export const MAX_LENGTH_5000 = maxLength(5000);

export const minLength =
  (min: number) =>
  (value: string | undefined): string | undefined =>
    value && value.length > 0 && value.length < min
      ? `Must be at least ${min} characters`
      : undefined;

export const MIN_LENGTH_50 = minLength(50);
export const MIN_LENGTH_200 = minLength(200);

export const PASSWORD = (value: string | undefined): validationMsg.PASSWORD | undefined =>
  value && value.length < 8 ? validationMsg.PASSWORD : undefined;

export const SLUG = (value: string | undefined): validationMsg.SLUG | undefined =>
  value && !SLUG_REGEX.test(value) ? validationMsg.SLUG : undefined;

// https://github.com/storybookjs/storybook/issues/12208
// eslint-disable-next-line dot-notation, @typescript-eslint/dot-notation
export const nowPlus10Years = dayjs()['add'](10, 'y');

export const DATE_WITHIN_VALID_RANGE = (
  value: DayjsConfigType | undefined
): validationMsg.DATE_IS_BEFORE | string | undefined => {
  if (value) {
    const date = dayjs.utc(value, 'MM/DD/YYYY');
    if (date.isBefore('2012')) {
      return validationMsg.DATE_IS_BEFORE;
    }
    if (date.isAfter(nowPlus10Years)) {
      return `Date cannot be after ${nowPlus10Years.format('YYYY')}`;
    }
  }

  return undefined;
};

export const DATE_AFTER_NOW = (
  value: DayjsConfigType | undefined
): validationMsg.DATE_NON_VALID | validationMsg.DATE_PAST | string | undefined => {
  if (value) {
    const date = dayjs.utc(value, 'MM/DD/YYYY');
    const now = dayjs().format('MM/DD/YYYY');
    const nowInUTC = dayjs.utc(now, 'MM/DD/YYYY');

    if (!date.isValid()) {
      return validationMsg.DATE_NON_VALID;
    }
    if (date.isBefore(nowInUTC)) {
      return validationMsg.DATE_PAST;
    }
    if (date.isAfter(nowPlus10Years)) {
      return `Date cannot be after ${nowPlus10Years.format('YYYY')}`;
    }
  }

  return undefined;
};

export const URL = (value: string | undefined): validationMsg.URL | undefined =>
  value && !URL_REGEX.test(value) ? validationMsg.URL : undefined;

export const TOP_LEVEL_DOMAIN = (value: string | undefined): validationMsg.URL | undefined =>
  value && !TOP_LEVEL_DOMAIN_REGEX.test(value) ? validationMsg.URL : undefined;

export const INSTAGRAM_HANDLE = (value: string | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return /^@?(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/gim.test(value)
    ? undefined
    : validationMsg.INSTAGRAM_HANDLE;
};

/** Validates the value represents an integer */
export const NUMBER = (value: string | number | undefined): string | undefined => {
  const number = Number(value);
  const isNumber = _isInteger(number);
  if (value && !isNumber) {
    return validationMsg.NUMBER;
  }
  return undefined;
};

/** Validates the value, if anything other than undefined, represents an integer greater than zero */
export const GREATER_THAN_ZERO = (value: string | number | undefined): string | undefined => {
  const number = Number(value);
  const isNumber = _isInteger(number);
  if (isNumber && number <= 0) {
    return validationMsg.GREATER_THAN_ZERO;
  }
  return undefined;
};

/** Validates the value, if anything other than undefined, null, or empty string, represents an integer greater than zero */
export const GREATER_THAN_ZERO_OR_BLANK = (
  value: string | undefined | null | number
): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return GREATER_THAN_ZERO(value);
};

/** Validates the value, if anything other than undefined, null, or empty string, represents an integer greater than or equal to zero */
export const GREATER_THAN_OR_EQUAL_TO_ZERO = (
  value: string | undefined | null | number
): string | undefined => {
  const number = Number(value);
  const isNumber = _isInteger(number);
  if (isNumber && number < 0) {
    return validationMsg.GREATER_THAN_OR_EQUAL_TO_ZERO;
  }
  return undefined;
};

export const DOES_NOT_CONTAIN_EMOJI = (value: string | undefined): string | undefined =>
  value && EMOJI_REGEX.test(value) ? validationMsg.EMOJI : undefined;

export const CONTAINS_FIRST_AND_LAST_NAME = (value: string | undefined): string | undefined => {
  return value && value.trim() && FIRST_AND_LAST_NAME_REGEX.test(value.trim())
    ? undefined
    : validationMsg.FIRST_AND_LAST_NAME;
};

/** Validates the value, if anything other than undefined, null, or empty string, represents an integer less than 2 billion */
export const MAX_GUEST_COUNT = (value: string | undefined | null | number): string | undefined => {
  const number = Number(value);
  const isNumber = _isInteger(number);
  // Max Int on our BE is 2147483647, but just round down to 2 billion.
  if (isNumber && number > 2000000000) {
    return validationMsg.MAX_GUEST_COUNT;
  }
  return undefined;
};
